/**
 * Which slide the study page opens, and which the compare panes show — pure,
 * off the page's state, so the order of arrival (QIDO before the navigator,
 * or after) is tested rather than hoped for.
 */

import { uidSuffix } from '../share/api';

interface OpenableSeries {
  seriesInstanceUID: string;
  modality: string;
}

/**
 * The slide to open first, or undefined to wait.
 *
 * A NAMED slide — a share link's suffix, or `?series=` — opens at once. Any
 * other study waits for the navigator, whose first image is the one to open:
 * chosen before it answers, the page would open an interim slide, start its
 * viewer, and (through a share link) write that slide into the address. Once
 * it has answered: its first card; where it listed nothing (no navigator
 * here), the old rule — the first slide-microscopy series, else the first.
 */
export function initialSlide({
  series,
  suffixes,
  slide,
  wanted,
  navigatorSettled,
  ordered,
}: {
  /** In navigator order, so the first is the navigator's first when it has one. */
  series: OpenableSeries[];
  /** `uidSuffix` of each series, by index. */
  suffixes: string[];
  /** A share link's slide suffix; undefined outside a link. */
  slide: string | undefined;
  /** `?series=` */
  wanted: string | null;
  navigatorSettled: boolean;
  /** Whether the navigator listed images, so `series` is in its order: then the first is its first card. */
  ordered: boolean;
}): string | undefined {
  if (series.length === 0) return undefined;
  const named = slide !== undefined ? series[suffixes.indexOf(slide)] : wanted ? series.find((s) => s.seriesInstanceUID === wanted) : undefined;
  if (named) return named.seriesInstanceUID;
  if (!navigatorSettled) return undefined;
  if (ordered) return series[0].seriesInstanceUID;
  return (series.find((s) => s.modality === 'SM') ?? series[0]).seriesInstanceUID;
}

/**
 * The slide the page is going to open on, as far as it can tell before QIDO
 * has listed the study: the one `?series=` names; a share link's suffix once
 * the navigator lists the slide it names; else the navigator's first card;
 * else nothing yet. Its metadata is asked for at once (StudyPage), one round
 * trip before initialSlide can settle on it.
 */
export function expectedSlide({
  wanted,
  slide,
  images,
}: {
  /** `?series=` */
  wanted: string | null;
  /** A share link's slide suffix; undefined outside a link. */
  slide: string | undefined;
  /** The navigator's images, in display order. */
  images: readonly { series_instance_uid: string }[];
}): string | undefined {
  // The same order as initialSlide: inside a share link its slide decides, whatever `?series=` says.
  if (slide !== undefined) return images.find((img) => uidSuffix(img.series_instance_uid) === slide)?.series_instance_uid;
  if (wanted) return wanted;
  return images[0]?.series_instance_uid;
}

/**
 * The series each compare pane shows, as list indexes, from the URL's pane
 * values — a slide's `uidSuffix`, which survives a reorder where a list index
 * would not — defaulting to distinct slides: the one you were looking at, then
 * the next ones not already shown. No slide is in two panes, even where the
 * address names it twice: one slide shown twice reads as two slides, and
 * whatever is drawn on it turns up in both.
 */
export function resolvePanes(values: (string | null)[], suffixes: string[], selectedIndex: number): number[] {
  const taken = new Set<number>();
  const named = values.map((v) => {
    const i = v ? suffixes.indexOf(v) : -1;
    if (i < 0 || taken.has(i)) return -1;
    taken.add(i);
    return i;
  });
  return named.map((n, i) => {
    if (n >= 0) return n;
    const pick =
      i === 0 && !taken.has(Math.max(0, selectedIndex))
        ? Math.max(0, selectedIndex)
        : suffixes.findIndex((_, u) => !taken.has(u));
    taken.add(pick);
    return pick;
  });
}

/**
 * The series compare may show, in list order: every picture, never a document
 * (a PDF opens in its own view, which has no panes). `kindOf` is the
 * navigator's word for a series, undefined for one it does not know.
 */
export function comparableSeries(uids: readonly string[], kindOf: (uid: string) => string | undefined): string[] {
  return uids.filter((uid) => kindOf(uid) !== 'document');
}

/** Entering compare: the selected slide, then the next distinct ones, `count` in all. */
export function firstPanes(selectedIndex: number, count: number, total: number): number[] {
  const chosen: number[] = [Math.max(0, selectedIndex)];
  while (chosen.length < count) chosen.push(Array.from({ length: total }, (_, i) => i).findIndex((i) => !chosen.includes(i)));
  return chosen;
}

/** The compare grid is 2×2: four panes at most (the pane letters A–D and the
 *  address's a–d are sized to it). */
export const GRID_CAPACITY = 4;
/** It opens for three slides, the last cell then empty. Two stay a pair: laid
 *  out 2×2 they would leave a whole row empty. */
const GRID_MINIMUM = 3;

/**
 * How many panes the grid shows a study of `total` slides: none below three
 * (the grid is not offered, and a `layout=g` that cannot be honoured — a
 * remembered arrangement carried onto a two-slide study, or a pasted link —
 * falls back to a pair), else every slide up to the grid's four.
 */
export function gridPaneCount(total: number): number {
  return total < GRID_MINIMUM ? 0 : Math.min(total, GRID_CAPACITY);
}
